import React, { cloneElement } from "react";
import {
  Button,
  Datagrid,
  Filter,
  FunctionField,
  ResourceContextProvider,
  RowClickFunction,
  sanitizeListRestProps,
  SearchInput,
  SelectInput,
  ShowProps,
  TextField,
  TopToolbar,
  useListContext,
} from "react-admin";
import IconPlus from "@material-ui/icons/Add";
import SanitizedGrid from "components/SanitizedGrid";
import { Grid, Typography } from "@material-ui/core";
import CustomEmptyComponent from "components/CustomEmptyComponent";
import { statusList } from "./const";
import StatusField from "components/StatusField";
import { PaddingDenseStyled2List } from "../../components/ListWrapper";
import CustomPagination from "../../components/CustomPagination";

enum AreaType {
  regularArea = "regular",
  ccaArea = "cca",
  subcontractorArea = "subcontractor",
  fixedLocation = "fixedLocation",
}

const ThisFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
  </Filter>
);

const FixedLocationFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
    <SelectInput label="Status" source="Status" choices={statusList} alwaysOn />
  </Filter>
);

const ListActions = (props: any) => {
  const {
    className,
    exporter,
    filters,
    maxResults,
    history,
    areaType,
    ...rest
  } = props;
  const { resource, displayedFilters, filterValues, showFilter } =
    useListContext();

  let actionUrl = "/createAreas";
  let buttonLabel = "Create Area";

  switch (areaType) {
    case AreaType.regularArea:
      actionUrl = `/areas/create?source=${JSON.stringify({
        schoolId: Number(props.filterValues?.SchoolId),
      })}&schoolId=${Number(props.filterValues?.SchoolId)}`;
      buttonLabel = "Create Regular School Area";
      break;
    case AreaType.ccaArea:
      actionUrl = `/ccaAreas/create?source=${JSON.stringify({
        schoolId: Number(props.filterValues?.SchoolId),
      })}&schoolId=${Number(props.filterValues?.SchoolId)}`;
      buttonLabel = "Create CCA School Area";
      break;
    case AreaType.subcontractorArea:
      actionUrl = `/subcontractorAreas/create?source=${JSON.stringify({
        schoolId: Number(props.filterValues?.SchoolId),
      })}&schoolId=${Number(props.filterValues?.SchoolId)}`;
      buttonLabel = "Create Subcontractor Area";
      break;
    case AreaType.fixedLocation:
      actionUrl = `/ccaFixedLocationDropOffs/create?source=${JSON.stringify({
        schoolId: Number(props.filterValues?.SchoolId),
      })}&schoolId=${Number(props.filterValues?.SchoolId)}`;
      buttonLabel = "Create CCA Fixed Location Drop Off";
      break;
    default:
      break;
  }

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      {filters &&
        cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: "button",
        })}

      <Button onClick={() => history.push(actionUrl)} label={buttonLabel}>
        <IconPlus />
      </Button>
    </TopToolbar>
  );
};

const CustomDatagrid = ({ rowClick, isSortAble }: any) => {
  const { data } = useListContext();

  const formatData = (res: any) => {
    if (res && !isSortAble) {
      Object.keys(res)
        .sort((a: any, b: any) => {
          return res[a].maxDistanceInMeters - res[b].maxDistanceInMeters;
        })
        .map((key: any, index) => {
          const item = res[key];
          if (item) {
            item.name = index + 1;
          }
        });

      return res;
    }
  };

  return (
    <Datagrid data={formatData(data)} rowClick={rowClick}>
      <TextField source="name" label="Name" sortable={isSortAble} />
      <TextField source="maxDistanceInMeters" sortable={isSortAble} />
    </Datagrid>
  );
};

const FixedLocationDatagrid = ({ rowClick }: any) => {
  const { data } = useListContext();

  return (
    <Datagrid data={data} rowClick={rowClick}>
      <TextField source="name" />
      <TextField source="address" />
      <StatusField source="status" />
    </Datagrid>
  );
};

const CcaAreaAndFixedLocation = (props: ShowProps) => {
  const rowClickCca: RowClickFunction = (id) =>
    `/ccaAreas/${id}?schoolId=${props.id}`;
  const rowClickFixedLocation: RowClickFunction = (id) =>
    `/ccaFixedLocationDropOffs/${id}?schoolId=${props.id}`;

  return (
    <FunctionField
      render={(record: any) =>
        record.isCcaSettingEnable ? (
          <>
            <Grid item xs={12} style={{ marginTop: 30 }}>
              <Typography variant="h6">CCA School Areas</Typography>
            </Grid>
            <Grid item xs={12}>
              <ResourceContextProvider value="ccaAreas">
                <PaddingDenseStyled2List
                  {...props}
                  exporter={false}
                  hasCreate={false}
                  filters={<ThisFilter />}
                  filterDefaultValues={{ SchoolId: parseInt(props.id) }}
                  sort={{ field: "maxDistanceInMeters", order: "ASC" }}
                  bulkActionButtons={false}
                  title=" "
                  actions={
                    <ListActions
                      history={props.history}
                      areaType={AreaType.ccaArea}
                    />
                  }
                  pagination={<CustomPagination />}
                >
                  <CustomDatagrid rowClick={rowClickCca} isSortAble={true} />
                </PaddingDenseStyled2List>
              </ResourceContextProvider>
            </Grid>

            <Grid item xs={12} style={{ marginTop: 30 }}>
              <Typography variant="h6">CCA Fixed Location Drop Offs</Typography>
            </Grid>
            <Grid item xs={12}>
              <ResourceContextProvider value="ccaFixedLocationDropOffs">
                <PaddingDenseStyled2List
                  {...props}
                  exporter={false}
                  hasCreate={false}
                  filters={<FixedLocationFilter />}
                  filterDefaultValues={{ SchoolId: parseInt(props.id) }}
                  bulkActionButtons={false}
                  title=" "
                  actions={
                    <ListActions
                      history={props.history}
                      areaType={AreaType.fixedLocation}
                    />
                  }
                  pagination={<CustomPagination />}
                >
                  <FixedLocationDatagrid rowClick={rowClickFixedLocation} />
                </PaddingDenseStyled2List>
              </ResourceContextProvider>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} style={{ marginTop: 30 }}>
              <Typography variant="h6">
                CCA School Areas and CCA Fixed Location Drop Off
              </Typography>
            </Grid>
            <CustomEmptyComponent
              resource="ccaAreas"
              title="The CCA setting is disabled"
              caption="Please enable the CCA setting in the school setting summary to show the CCA school area and CCA fixed location drop off."
              hasCreate={false}
            />
          </>
        )
      }
    />
  );
};

const DistanceArea = (props: ShowProps) => {
  const rowClick: RowClickFunction = (id) =>
    `/areas/${id}?schoolId=${props.id}`;
  const rowClickSubcontractor: RowClickFunction = (id) =>
    `/subcontractorAreas/${id}?schoolId=${props.id}`;

  return (
    <SanitizedGrid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Regular School Areas</Typography>
      </Grid>
      <Grid item xs={12}>
        <ResourceContextProvider value="areas">
          <PaddingDenseStyled2List
            {...props}
            exporter={false}
            hasCreate={false}
            filters={<ThisFilter />}
            filterDefaultValues={{ SchoolId: parseInt(props.id) }}
            bulkActionButtons={false}
            title=" "
            actions={
              <ListActions
                history={props.history}
                areaType={AreaType.regularArea}
              />
            }
            pagination={<CustomPagination />}
          >
            <Datagrid rowClick={rowClick}>
              <TextField source="name" />
              <TextField source="maxDistanceInMeters" />
            </Datagrid>
          </PaddingDenseStyled2List>
        </ResourceContextProvider>
      </Grid>

      <Grid item xs={12}>
        <CcaAreaAndFixedLocation {...props} />
      </Grid>

      <Grid item xs={12} style={{ marginTop: 30 }}>
        <Typography variant="h6">Subcontractor Areas</Typography>
      </Grid>
      <Grid item xs={12}>
        <ResourceContextProvider value="subcontractorAreas">
          <PaddingDenseStyled2List
            {...props}
            exporter={false}
            hasCreate={false}
            filters={<ThisFilter />}
            filterDefaultValues={{ SchoolId: parseInt(props.id) }}
            sort={{ field: "maxDistanceInMeters", order: "ASC" }}
            bulkActionButtons={false}
            title=" "
            actions={
              <ListActions
                history={props.history}
                areaType={AreaType.subcontractorArea}
              />
            }
            pagination={<CustomPagination />}
          >
            <CustomDatagrid
              rowClick={rowClickSubcontractor}
              isSortAble={false}
            />
          </PaddingDenseStyled2List>
        </ResourceContextProvider>
      </Grid>
    </SanitizedGrid>
  );
};

export default DistanceArea;
